import React from 'react';
import PropTypes from 'prop-types';

import {
  extractBookTitles,
  identifyBookMentions,
  lookupBookCover,
  manualBookTitles,
  yomerebaSearchUrl,
} from './utils';

const BookMention = ({ content }) => {
  const curatedTitles = React.useMemo(() => manualBookTitles(content), [content]);
  const fallbackTitles = React.useMemo(
    () => (curatedTitles.length ? curatedTitles : extractBookTitles(content)),
    [content, curatedTitles],
  );
  const [titles, setTitles] = React.useState(fallbackTitles);
  const [books, setBooks] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadTitles() {
      if (curatedTitles.length) {
        setTitles(curatedTitles);
        return;
      }

      try {
        const identifiedTitles = await identifyBookMentions(content);
        if (!cancelled) {
          setTitles(identifiedTitles.length ? identifiedTitles : fallbackTitles);
        }
      } catch {
        if (!cancelled) setTitles(fallbackTitles);
      }
    }

    setTitles(fallbackTitles);
    loadTitles();

    return () => {
      cancelled = true;
    };
  }, [content, curatedTitles, fallbackTitles]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadBooks() {
      const results = await Promise.all(
        titles.map(async (title) => {
          try {
            return lookupBookCover(title);
          } catch {
            return {
              title,
              matchedTitle: title,
              authors: [],
              publisher: '',
              image: '',
              link: yomerebaSearchUrl(title),
            };
          }
        }),
      );

      if (!cancelled) setBooks(results);
    }

    if (titles.length) {
      loadBooks();
    } else {
      setBooks([]);
    }

    return () => {
      cancelled = true;
    };
  }, [titles]);

  if (!titles.length) return null;

  return (
    <aside className="ehon-book-box">
      <span className="ehon-section-label">この記事に出てくる本</span>
      <div className="ehon-book-list">
        {(books.length ? books : titles.map((title) => ({ title }))).map(
          (book) => (
            <a
              className="ehon-book-link"
              href={book.link || yomerebaSearchUrl(book.title)}
              key={book.title}
              rel="noopener noreferrer"
              target="_blank"
            >
              {book.image ? (
                <img src={book.image} alt={`${book.matchedTitle} の表紙`} />
              ) : (
                <span className="ehon-book-placeholder">本</span>
              )}
              <span>
                <strong>{book.matchedTitle || book.title}</strong>
                {book.authors?.length ? <small>{book.authors.join('、')}</small> : null}
                {book.publisher ? <small>{book.publisher}</small> : null}
              </span>
            </a>
          ),
        )}
      </div>
    </aside>
  );
};

BookMention.propTypes = {
  content: PropTypes.shape({
    blocks: PropTypes.object,
    blocks_layout: PropTypes.object,
    title: PropTypes.string,
  }).isRequired,
};

export default BookMention;
